import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import api, { formatApiError } from "@/lib/api";
import { mediaPublicUrl } from "@/lib/mediaUrl";

const MAX_VIDEO_BYTES = 70 * 1024 * 1024;

const Video = Node.create({
  name: "video",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
    };
  },
  parseHTML() {
    return [{ tag: "video[src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "video",
      mergeAttributes(HTMLAttributes, {
        controls: "controls",
        playsinline: "playsinline",
        preload: "metadata",
        class: "blog-inline-video",
        style: "max-width:100%;width:100%;border-radius:12px;margin:1rem 0;background:#000;",
      }),
    ];
  },
  addCommands() {
    return {
      setVideo:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

const Btn = ({ active, disabled, onClick, title, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      "h-8 w-8 rounded-md flex items-center justify-center text-white/70 transition-colors",
      active ? "bg-white/15 text-white shadow-[inset_0_0_0_1px_rgba(139,92,246,0.35)]"
             : "hover:bg-white/[0.06] hover:text-white",
      disabled && "opacity-40 cursor-not-allowed"
    )}
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-5 bg-white/10 mx-1" />;

export default function RichTextEditor({ value, onChange, placeholder = "Start writing…", testId }) {
  const imageRef = useRef(null);
  const videoRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false, underline: false }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "text-[#93C5FD] underline hover:text-[#C4B5FD]" },
      }),
      Image.configure({ HTMLAttributes: { class: "rounded-lg my-3" } }),
      Video,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose-tiptap outline-none min-h-[220px] px-4 py-3 text-white/90",
        "data-testid": testId ? `${testId}-editor` : "richtext-editor",
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const insertLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  const uploadImage = async (file) => {
    if (!file || !editor) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "blog");
      const { data } = await api.post("/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 10 * 60 * 1000,
      });
      const src = mediaPublicUrl(data);
      editor.chain().focus().setImage({ src }).run();
      toast.success("Image inserted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadVideo = async (file) => {
    if (!file || !editor) return;
    if (!String(file.type || "").startsWith("video/") && !/\.(mp4|webm|mov|m4v)$/i.test(file.name || "")) {
      toast.error("Please choose a video file (MP4, WebM, or MOV)");
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error("Video too large. Max is 70MB.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "blog");
      const { data } = await api.post("/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 10 * 60 * 1000,
      });
      const src = mediaPublicUrl(data);
      editor.chain().focus().setVideo({ src }).run();
      toast.success("Video inserted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Video upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-black/20 overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-white/10 bg-white/[0.02]">
        <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold" testId="rt-bold">
          <Icons.Bold className="w-3.5 h-3.5" />
        </Btn>
        <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic" testId="rt-italic">
          <Icons.Italic className="w-3.5 h-3.5" />
        </Btn>
        <Btn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline" testId="rt-underline">
          <Icons.Underline className="w-3.5 h-3.5" />
        </Btn>
        <Btn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
          <Icons.Strikethrough className="w-3.5 h-3.5" />
        </Btn>
        <Divider />
        <Btn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1" testId="rt-h1">
          <Icons.Heading1 className="w-3.5 h-3.5" />
        </Btn>
        <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
          <Icons.Heading2 className="w-3.5 h-3.5" />
        </Btn>
        <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
          <Icons.Heading3 className="w-3.5 h-3.5" />
        </Btn>
        <Divider />
        <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bulleted list" testId="rt-ul">
          <Icons.List className="w-3.5 h-3.5" />
        </Btn>
        <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">
          <Icons.ListOrdered className="w-3.5 h-3.5" />
        </Btn>
        <Btn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote">
          <Icons.Quote className="w-3.5 h-3.5" />
        </Btn>
        <Btn active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code block">
          <Icons.Code2 className="w-3.5 h-3.5" />
        </Btn>
        <Divider />
        <Btn active={editor.isActive("link")} onClick={insertLink} title="Insert link" testId="rt-link">
          <Icons.Link className="w-3.5 h-3.5" />
        </Btn>
        <input
          ref={imageRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ""; }}
        />
        <input
          ref={videoRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
          className="hidden"
          onChange={(e) => { uploadVideo(e.target.files?.[0]); e.target.value = ""; }}
        />
        <Btn disabled={uploading} onClick={() => imageRef.current?.click()} title={uploading ? "Uploading…" : "Upload image"}>
          <Icons.Image className="w-3.5 h-3.5" />
        </Btn>
        <Btn disabled={uploading} onClick={() => videoRef.current?.click()} title={uploading ? "Uploading…" : "Upload video (max 70MB)"} testId="rt-video">
          <Icons.Film className="w-3.5 h-3.5" />
        </Btn>
        <Divider />
        <Btn disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Icons.Undo2 className="w-3.5 h-3.5" />
        </Btn>
        <Btn disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Icons.Redo2 className="w-3.5 h-3.5" />
        </Btn>
        <div className="ml-auto text-[10px] uppercase tracking-widest text-white/30 pr-2 hidden sm:block">
          Rich text · video ≤70MB
        </div>
      </div>
      <EditorContent editor={editor} data-testid={testId} />
    </div>
  );
}
